import { useState, useEffect, useRef } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function TranscriptView() {
  const [transcript, setTranscript] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showStatementOfFaith, setShowStatementOfFaith] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    fetchTranscript();
  }, []);

  const fetchTranscript = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/celestial/transcript/dual-label`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTranscript(response.data);
    } catch (err) {
      setError(
        err.response?.data?.detail || "Error loading transcript. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="card">
        <h3>Academic Transcript</h3>
        <div style={{ textAlign: "center", padding: "40px", color: "#FFD700" }}>
          <div style={{ fontSize: "2rem", marginBottom: "10px" }}>📜</div>
          <p>Loading Dual-Label Transcript...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card">
        <h3>Academic Transcript</h3>
        <div style={{ 
          background: "#4d1a1a", 
          border: "1px solid #ff6b6b", 
          padding: "15px", 
          borderRadius: "8px",
          color: "#ff6b6b"
        }}>
          {error}
        </div>
      </div>
    );
  }

  const transcriptData = transcript?.transcript;
  const statementOfFaith = transcript?.statement_of_faith;
  const complianceNotes = transcript?.compliance_notes || [];
  const calendarRef = transcript?.calendar_reference;

  return (
    <div className="card" data-testid="transcript-view" ref={printRef}>
      {/* Header */}
      <div style={{ 
        background: "linear-gradient(135deg, #2a1a0a 0%, #1a1a3a 100%)",
        padding: "20px",
        borderRadius: "10px",
        marginBottom: "20px",
        border: "2px solid #FFD700"
      }}>
        <h2 style={{ color: "#FFD700", margin: 0, textAlign: "center" }}>
          📜 Official Academic Transcript
        </h2>
        <p style={{ color: "#CD853F", textAlign: "center", margin: "10px 0 0 0" }}>
          Yisrael Learning Center - Dual-Label Format
        </p>
        <p style={{ color: "#888", textAlign: "center", fontSize: "0.8rem", margin: "5px 0 0 0" }}>
          50-State Educational Compliance
        </p>
      </div>

      {/* Student Info */}
      <div style={{ 
        display: "grid", 
        gridTemplateColumns: "1fr 1fr",
        gap: "15px",
        marginBottom: "20px"
      }}>
        <div style={{ 
          background: "#111", 
          padding: "15px", 
          borderRadius: "8px",
          border: "1px solid #333"
        }}>
          <h4 style={{ color: "#FFD700", margin: "0 0 10px 0", fontSize: "0.9rem" }}>
            Student Information
          </h4>
          <p style={{ color: "#fff", margin: "5px 0" }}>
            <strong>Name:</strong> {transcriptData?.student_name || "N/A"}
          </p>
          <p style={{ color: "#aaa", margin: "5px 0", fontSize: "0.85rem" }}>
            <strong>ID:</strong> {transcriptData?.student_id || "N/A"}
          </p>
          <p style={{ color: "#aaa", margin: "5px 0", fontSize: "0.85rem" }}>
            <strong>Grade Level:</strong> {transcriptData?.grade_level || "Not Set"}
          </p>
        </div>
        
        <div style={{ 
          background: "#111", 
          padding: "15px", 
          borderRadius: "8px",
          border: "1px solid #333"
        }}>
          <h4 style={{ color: "#FFD700", margin: "0 0 10px 0", fontSize: "0.9rem" }}>
            Transcript Generated
          </h4>
          <p style={{ color: "#fff", margin: "5px 0", fontSize: "0.85rem" }}>
            <strong>Gregorian:</strong> {transcriptData?.generated_date?.gregorian || "N/A"}
          </p>
          <p style={{ color: "#CD853F", margin: "5px 0", fontSize: "0.85rem" }}>
            <strong>Biblical:</strong> {transcriptData?.generated_date?.biblical || "N/A"}
          </p>
        </div>
      </div>

      {/* Current Biblical Calendar Status */}
      {calendarRef && (
        <div style={{
          background: "linear-gradient(135deg, #1a2a3a 0%, #111 100%)",
          padding: "12px 15px",
          borderRadius: "8px",
          marginBottom: "20px",
          border: "1px solid #4169E1"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <span style={{ color: "#87CEEB", fontSize: "0.8rem" }}>Current Biblical Date: </span>
              <span style={{ color: "#fff", fontWeight: "bold" }}>
                Day {calendarRef.current_biblical_date?.day} of {calendarRef.current_biblical_date?.month_name}
              </span>
            </div>
            <div>
              {calendarRef.sabbath_status?.is_sabbath && (
                <span style={{ 
                  background: "#8B4513", 
                  color: "#FFD700", 
                  padding: "4px 10px", 
                  borderRadius: "4px",
                  fontSize: "0.75rem"
                }}>
                  🕊️ Sabbath Day
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Transcript Entries */}
      <h4 style={{ color: "#FFD700", margin: "0 0 15px 0" }}>
        Academic Records ({transcriptData?.total_exams || 0} Assessments)
      </h4>

      {transcriptData?.entries?.length === 0 ? (
        <div style={{
          background: "#1a1a2e",
          padding: "30px",
          borderRadius: "8px",
          textAlign: "center",
          color: "#888"
        }}>
          <p>No exam records yet. Complete assessments to build your transcript.</p>
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid #FFD700" }}>
                <th style={{ padding: "12px", textAlign: "left", color: "#FFD700" }}>Assessment</th>
                <th style={{ padding: "12px", textAlign: "center", color: "#FFD700" }}>Score</th>
                <th style={{ padding: "12px", textAlign: "center", color: "#FFD700" }}>Status</th>
                <th style={{ padding: "12px", textAlign: "left", color: "#FFD700" }}>
                  Date (Gregorian / Biblical)
                </th>
              </tr>
            </thead>
            <tbody>
              {transcriptData?.entries?.filter(e => e.type === "exam").map((record, index) => (
                <tr 
                  key={index} 
                  data-testid={`transcript-row-${index}`}
                  style={{ borderBottom: "1px solid #333" }}
                >
                  <td style={{ padding: "12px" }}>
                    <div style={{ color: "#fff" }}>{record.name}</div>
                    <div style={{ color: "#888", fontSize: "0.8rem" }}>
                      {record.grade_level && `${record.grade_level} • `}{record.credit_type}
                    </div>
                  </td>
                  <td style={{ padding: "12px", textAlign: "center" }}>
                    <strong style={{ 
                      color: record.passed ? "#90EE90" : "#FF6B6B",
                      fontSize: "1.1rem"
                    }}>
                      {record.score}%
                    </strong>
                  </td>
                  <td style={{ padding: "12px", textAlign: "center" }}>
                    <span
                      style={{
                        padding: "4px 12px",
                        borderRadius: "4px",
                        fontSize: "0.85rem",
                        background: record.passed ? "#1a4d1a" : "#4d1a1a",
                        color: record.passed ? "#90EE90" : "#FF6B6B"
                      }}
                    >
                      {record.passed ? "PASSED" : "IN PROGRESS"}
                    </span>
                  </td>
                  <td style={{ padding: "12px" }}>
                    <div style={{ color: "#fff", fontSize: "0.85rem" }}>
                      {record.date?.gregorian || "N/A"}
                    </div>
                    <div style={{ color: "#CD853F", fontSize: "0.8rem" }}>
                      {record.date?.biblical || "N/A"}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Coursework in Progress */}
      {transcriptData?.entries?.filter(e => e.type === "coursework").length > 0 && (
        <>
          <h4 style={{ color: "#FFD700", margin: "25px 0 15px 0" }}>
            Coursework in Progress
          </h4>
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {transcriptData?.entries?.filter(e => e.type === "coursework").map((course, index) => (
              <div 
                key={index}
                style={{
                  background: "#1a1a2e",
                  padding: "12px 15px",
                  borderRadius: "6px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center"
                }}
              >
                <div>
                  <div style={{ color: "#fff" }}>{course.name}</div>
                  <div style={{ color: "#888", fontSize: "0.8rem" }}>
                    {course.grade_level} • {course.lessons_completed} lessons completed
                  </div>
                </div>
                <span style={{ 
                  background: "#2a3a4a", 
                  color: "#87CEEB", 
                  padding: "4px 10px", 
                  borderRadius: "4px",
                  fontSize: "0.8rem"
                }}>
                  In Progress
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Statement of Faith Toggle */}
      <div style={{ marginTop: "25px" }}>
        <button
          onClick={() => setShowStatementOfFaith(!showStatementOfFaith)}
          style={{
            background: "#2a1a0a",
            border: "1px solid #CD853F",
            borderRadius: "6px",
            padding: "10px 20px",
            color: "#CD853F",
            cursor: "pointer",
            width: "100%",
            textAlign: "left",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}
        >
          <span>📜 YLC Statement of Faith</span>
          <span>{showStatementOfFaith ? "▲" : "▼"}</span>
        </button>
        
        {showStatementOfFaith && (
          <div style={{
            background: "#111",
            padding: "20px",
            borderRadius: "0 0 8px 8px",
            border: "1px solid #CD853F",
            borderTop: "none",
            whiteSpace: "pre-line",
            color: "#ccc",
            fontSize: "0.85rem",
            lineHeight: "1.6"
          }}>
            {statementOfFaith}
          </div>
        )}
      </div>

      {/* Compliance Notes */}
      <div style={{
        background: "#0a1a0a",
        padding: "15px",
        borderRadius: "8px",
        marginTop: "20px",
        border: "1px solid #2a4a2a"
      }}>
        <h5 style={{ color: "#90EE90", margin: "0 0 10px 0", fontSize: "0.85rem" }}>
          Compliance Notes
        </h5>
        <ul style={{ margin: 0, paddingLeft: "20px", color: "#aaa", fontSize: "0.8rem" }}>
          {complianceNotes.map((note, idx) => (
            <li key={idx} style={{ marginBottom: "5px" }}>{note}</li>
          ))}
        </ul>
      </div>

      {/* Print Button */}
      <div style={{ marginTop: "20px", textAlign: "center", display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
        <button
          data-testid="print-transcript-btn"
          onClick={handlePrint}
          style={{
            background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
            border: "none",
            borderRadius: "8px",
            padding: "12px 30px",
            color: "#000",
            fontWeight: "bold",
            cursor: "pointer"
          }}
        >
          🖨️ Print Transcript
        </button>
        <button
          data-testid="download-transcript-html"
          onClick={() => {
            const html = printRef.current?.outerHTML || "";
            const doc = `<!doctype html><html><head><meta charset="utf-8"><title>YLC Transcript — ${transcriptData?.student_name || ""}</title>
<style>body{font-family:Georgia,serif;background:#fff;color:#000;padding:32px}table{width:100%;border-collapse:collapse;margin-top:12px}th,td{padding:8px;border-bottom:1px solid #ccc;text-align:left}h2,h3,h4{color:#8B4513}[data-testid="transcript-view"]{background:#fff !important;color:#000 !important}button{display:none !important}</style></head><body>${html}</body></html>`;
            const blob = new Blob([doc], { type: "text/html;charset=utf-8" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `ylc-transcript-${(transcriptData?.student_name || "student").replace(/\s+/g, "-").toLowerCase()}.html`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
          }}
          style={{
            background: "#87CEEB",
            border: "none",
            borderRadius: 8,
            padding: "12px 24px",
            color: "#000",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          📥 Download Transcript (HTML)
        </button>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          [data-testid="transcript-view"], 
          [data-testid="transcript-view"] * {
            visibility: visible;
          }
          [data-testid="transcript-view"] {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            background: white !important;
            color: black !important;
          }
          button {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}

export default TranscriptView;
